<template>
  <div class="expenses-container">
    <base-nav title="Расходы">
      <base-button @click="editExpenseCategories">
        Редактировать категории
      </base-button>
      <base-button
        v-if="expenses.length > 0"
        @click="exportToExcel"
      >
        Экспорт в Excel
      </base-button>
      <base-button @click="showImportModal = true">
        Импорт из 1С
      </base-button>
      <base-primary-button
        icon="addSquare"
        @click="showAddExpensesModal = true"
      >
        Создать
      </base-primary-button>
    </base-nav>

    <!-- Фильтры -->
    <FilterBlock
      ref="filterBlock"
      :search-fields="searchFields"
      :initial-filters="appliedFilters"
      target-type="Expense"
      @filter-change="handleFilterChange"
    />

    <!-- Счетчик результатов -->
    <MetaResultCount
      v-if="!dataIsLoading && expenses && expenses.length > 0"
      :pagination-meta="pagination"
    />

    <!-- Кнопка действий для таблицы -->
    <TableToolbar
      v-if="!dataIsLoading && expenses.length > 0"
      :show-settings="true"
      :actions="dropdownItems"
      @open-settings="showSettingsModal = true"
    />

    <base-loader v-if="dataIsLoading" />

    <!-- Таблица расходов -->
    <div
      v-else-if="!dataIsLoading && expenses && expenses.length > 0"
      class="expenses-center"
    >
      <TableComponent
        :data="expenses"
        :columns="tableColumns"
        :ready="!dataIsLoading && tableColumns.length > 0"
        :sort-fields="sortFields"
        :server-sort="currentSort"
        :loading="sortIsLoading"
        :reset-selection="tableResetSelection"
        :extra-context-menu-items="tableContextMenuItems"
        selectable
        @selection-change="onSelectionChange"
        @row-click="editExpense"
        @row-middle-click="openExpenseInNewTab"
        @sort-change="handleSortChange"
        @accept-expense="handleAcceptExpense"
        @vehicle-link-click="goToVehicleDetails"
        @counterparty-link-click="goToCounterpartyDetails"
        @context-menu-action="handleTableContextMenuAction"
      >
        <template v-slot:[`cell-expense_category.name`]="{ item }">
          <BaseStatusSelect
            compact
            :value="
              item.expense_category
                ? String(item.expense_category.id)
                : null
            "
            :label="
              item.expense_category
                ? `${item.expense_category.emoji} ${item.expense_category.name}`
                : '—'
            "
            color="#e5e7eb"
            :options="
              expenseCategoryStore.expenseCategorySelectOptions
            "
            @change="handleCategoryChange(item, $event)"
          />
        </template>
      </TableComponent>
    </div>

    <!-- Пагинация -->
    <pagination-component
      v-if="
        pagination.pages > 1 &&
        !dataIsLoading &&
        tableColumns.length > 0
      "
      :page="pagination.page"
      :pages="pagination.pages"
      :series="pagination.series"
      :route-query="currentRouteQuery"
    />

    <div
      class="no-data"
      v-if="!dataIsLoading && expenses.length == 0"
    >
      Список расходов пуст
    </div>

    <!-- Модальное окно настроек -->
    <Teleport to="body">
      <TableSettingsModal
        v-if="showSettingsModal"
        resource-model="Expense"
        record-type="Expense"
        table-name="expenses"
        @close="showSettingsModal = false"
        @config-saved="handleConfigSaved"
      />
    </Teleport>

    <!-- Модальное окно создания расхода -->
    <AddExpensesModal
      v-if="showAddExpensesModal"
      @close="showAddExpensesModal = false"
      @expense-created="handleExpenseCreated"
    />

    <!-- Модальное окно редактирования расхода -->
    <EditExpensesModal
      v-if="showEditExpensesModal && selectedExpenseId"
      :expense-id="selectedExpenseId"
      @close="closeEditModal"
      @expense-updated="handleExpenseUpdated"
      @expense-deleted="handleExpenseDeleted"
      @expense-accepted="handleExpenseAccepted"
    />

    <!-- Модальное окно импорта -->
    <ImportExpensesModal
      v-if="showImportModal"
      @close="showImportModal = false"
      @import-success="handleImportSuccess"
    />

    <!-- Массовая смена категории -->
    <BulkChangeCategoryModal
      v-if="showBulkChangeCategoryModal"
      :expenses="bulkCategoryTarget"
      @close="showBulkChangeCategoryModal = false"
      @changed="handleBulkCategoryChanged"
    />

    <!-- Массовая смена транспорта -->
    <BulkChangeVehicleModal
      v-if="showBulkChangeVehicleModal"
      :expenses="bulkVehicleTarget"
      @close="showBulkChangeVehicleModal = false"
      @changed="handleBulkVehicleChanged"
    />

    <Teleport to="body">
      <BaseDialog
        v-if="showAcceptDialog"
        :visible="showAcceptDialog"
        title="Подтвердить расход"
        message="Вы уверены, что хотите подтвердить этот расход?"
        @action="handleAcceptDialogAction"
      />
    </Teleport>
  </div>
</template>

<script>
import PaginationComponent from "@/components/PaginationComponent.vue";
import FilterBlock from "@/components/Filter/FilterBlock.vue";
import MetaResultCount from "@/components/Meta/MetaResultCount.vue";
import TableComponent from "@/components/TableComponent.vue";
import AddExpensesModal from "@/components/Expense/AddExpensesModal.vue";
import EditExpensesModal from "@/components/Expense/EditExpensesModal.vue";
import ImportExpensesModal from "@/components/Expense/ImportExpensesModal.vue";
import TableSettingsModal from "@/components/TableSettingsModal.vue";
import TableToolbar from "@/components/Table/TableToolbar.vue";
import BulkChangeCategoryModal from "@/components/Expense/BulkChangeCategoryModal.vue";
import BulkChangeVehicleModal from "@/components/Expense/BulkChangeVehicleModal.vue";

import { getCompanyId } from "@/utils/userUtils";
import { openRouteInNewTab } from "@/utils/routeUtils";
import { exportToExcel } from "@/utils/fileUtils";
import {
  hasQueryParam,
  getQueryParam,
  removeQueryParams,
} from "@/utils/urlUtils";

import { useNotificationStore } from "@/stores/notificationStore";
import { useTableConfigStore } from "@/stores/tableConfigStore";
import { useVehicleStore } from "@/stores/vehicleStore";
import { useExpenseCategoryStore } from "@/stores/expenseCategoryStore";

import companyUsersApi from "@/services/api/companyUsersApi";
import expensesApi from "@/services/api/expensesApi";

import {
  filtersToApiParams,
  getInitialFilters,
  getCurrentPageFromUrl,
  updateUrlWithFilters,
  clearUrlFilters,
} from "@/utils/filterUtils";

import rawSearchFields from "@/config/filters/expense/expenses.json";

export default {
  name: "ExpensesPage",
  components: {
    PaginationComponent,
    FilterBlock,
    MetaResultCount,
    TableComponent,
    AddExpensesModal,
    EditExpensesModal,
    ImportExpensesModal,
    TableSettingsModal,
    TableToolbar,
    BulkChangeCategoryModal,
    BulkChangeVehicleModal,
  },
  data() {
    return {
      companyId: getCompanyId(),
      dataIsLoading: true,
      sortIsLoading: false,
      showAddExpensesModal: false,
      showEditExpensesModal: false,
      showImportModal: false,
      selectedExpenseId: null,

      showSettingsModal: false,
      showBulkChangeCategoryModal: false,
      showBulkChangeVehicleModal: false,

      bulkCategoryTarget: [],
      bulkVehicleTarget: [],

      showAcceptDialog: false,
      pendingAcceptExpenseId: null,
      tableResetSelection: 0,

      expenses: [],
      company_users: [],
      tableColumns: [],
      selectedExpenses: [],

      sortFields: [
        "id",
        "expense_status",
        "amount",
        "spent_at",
        "expense_category.name",
        "vehicle",
        "created_at",
        "updated_at",
        "comment",
      ],

      currentSort: null,

      appliedFilters: {
        searchValues: {},
        showInactive: false,
      },

      dropdownItems: [
        {
          id: "duplicate",
          label: "Дублировать",
          action: this.duplicateSelected,
        },
        {
          id: "change-category",
          label: "Изменить категорию",
          action: this.openBulkChangeCategoryModal,
        },
        {
          id: "change-vehicle",
          label: "Изменить транспорт",
          action: this.openBulkChangeVehicleModal,
        },
      ],

      pagination: {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
        from: 0,
        to: 0,
      },
    };
  },
  watch: {
    "$route.query": {
      immediate: false,
      handler(newQuery, oldQuery) {
        // Не перезапрашиваем данные если изменился только expense_id
        const newWithoutExpense = { ...newQuery };
        const oldWithoutExpense = { ...oldQuery };
        delete newWithoutExpense.expense_id;
        delete oldWithoutExpense.expense_id;

        if (
          JSON.stringify(newWithoutExpense) !==
          JSON.stringify(oldWithoutExpense)
        ) {
          this.updateFiltersFromUrl();
          this.getExpensesRequest();
        }
      },
    },
  },
  computed: {
    notificationStore() {
      return useNotificationStore();
    },

    tableConfigStore() {
      return useTableConfigStore();
    },

    expenseCategoryStore() {
      return useExpenseCategoryStore();
    },

    currentRouteQuery() {
      return this.$route.query;
    },

    searchFields() {
      return rawSearchFields;
    },

    tableContextMenuItems() {
      return [
        {
          key: "change-category",
          label: "Изменить категорию",
          icon: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
      </svg>`,
        },
        {
          key: "change-vehicle",
          label: "Изменить транспорт",
          icon: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="1" y="3" width="15" height="13" rx="1"/>
        <path d="M16 8h4l3 5v3h-7V8z"/>
        <circle cx="5.5" cy="18.5" r="2.5"/>
        <circle cx="18.5" cy="18.5" r="2.5"/>
      </svg>`,
        },
        {
          key: "duplicate",
          label: "Дублировать",
          icon: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="9" y="9" width="13" height="13" rx="2"/>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
      </svg>`,
        },
      ];
    },
  },
  methods: {
    async loadTableConfig() {
      try {
        const columns =
          await this.tableConfigStore.initTableConfiguration(
            "Expense",
            "expenses",
            this.sortFields
          );

        this.tableColumns = columns;
      } catch (error) {
        console.error(
          "Не удалось загрузить конфигурацию таблицы расходов",
          error
        );
        this.tableColumns = [];
      }
    },

    handleConfigSaved() {
      this.loadTableConfig();
    },

    async getExpensesRequest({ sortOnly = false } = {}) {
      const page = getCurrentPageFromUrl(this.$route);

      try {
        if (sortOnly) {
          this.sortIsLoading = true;
        } else {
          this.dataIsLoading = true;
        }

        const params = {
          page,
          ...filtersToApiParams(
            this.appliedFilters,
            this.searchFields
          ),
        };

        if (this.currentSort) {
          params.q = {
            ...params.q,
            order_by: {
              field: this.currentSort.field,
              direction: this.currentSort.direction,
            },
          };
        }

        const vehicleStore = useVehicleStore();

        const [response] = await Promise.allSettled([
          expensesApi.index(this.companyId, params),
          vehicleStore.fetchVehicles(),
        ]).then((results) => {
          if (results[0].status === "rejected") {
            throw results[0].reason;
          }
          return [results[0].value];
        });

        const records = response.data.result;

        // Собираем уникальные vehicle_id из текущей страницы
        const vehicleIds = [
          ...new Set(
            records.map((e) => e.vehicle_id).filter(Boolean)
          ),
        ];

        // Догружаем те, которых нет в сторе после fetchVehicles
        await vehicleStore.ensureVehiclesLoaded(vehicleIds);

        this.expenses =
          this.tableConfigStore.enrichRecordsWithCustomFields(
            records,
            this.tableColumns
          );

        const meta = response.data.pagination_meta;
        this.pagination = {
          page: meta.page,
          pages: meta.pages,
          series: meta.series,
          next: meta.next,
          prev: meta.prev,
          count: meta.count,
          limit: meta.limit,
          from: meta.from,
          to: meta.to,
        };

        this.selectedExpenses = [];

        await this.getCompanyUsersRequest();
      } finally {
        this.dataIsLoading = false;
        this.sortIsLoading = false;
      }
    },

    async getCompanyUsersRequest() {
      try {
        const response = await companyUsersApi.index(this.companyId);
        this.company_users = response.data.result;
      } catch (error) {
        console.error(
          "Ошибка при загрузке пользователей компании:",
          error
        );
      }
    },

    updateFiltersFromUrl() {
      this.appliedFilters = getInitialFilters(
        this.$route,
        this.searchFields,
        {
          searchValues: {},
          showInactive: false,
        }
      );
    },

    handleFilterChange(filters) {
      this.appliedFilters = { ...filters };
      // Сбрасываем серверную сортировку при смене фильтров
      this.currentSort = null;

      const hasValues = Object.values(filters.searchValues).some(
        (v) =>
          v !== null && v !== undefined && v !== "" && v !== false
      );

      if (hasValues || filters.showInactive) {
        updateUrlWithFilters(this.$router, filters, 1);
      } else {
        clearUrlFilters(this.$router);
      }

      this.selectedExpenses = [];
    },

    handleSortChange({ field, direction }) {
      this.currentSort = { field, direction };
      this.getExpensesRequest({ sortOnly: true });
    },

    editExpenseCategories() {
      this.$router.push({ name: "ExpenseCategories" });
    },

    editExpense(expense) {
      const expenseId = expense.id || expense;
      this.selectedExpenseId = expenseId;
      this.showEditExpensesModal = true;
      this.$router.replace({
        query: { ...this.$route.query, expense_id: expenseId },
      });
    },

    closeEditModal() {
      this.showEditExpensesModal = false;
      this.selectedExpenseId = null;
      removeQueryParams("expense_id", this.$router, this.$route);
    },

    handleAcceptExpense(expenseId) {
      this.pendingAcceptExpenseId = expenseId;
      this.showAcceptDialog = true;
    },

    async handleAcceptDialogAction(actionKey) {
      this.showAcceptDialog = false;

      if (actionKey !== "confirm") {
        this.pendingAcceptExpenseId = null;
        return;
      }

      const expenseId = this.pendingAcceptExpenseId;
      this.pendingAcceptExpenseId = null;

      try {
        const response = await expensesApi.accept(
          this.companyId,
          expenseId
        );
        const updatedExpense = response.data.result;

        const index = this.expenses.findIndex(
          (e) => e.id === expenseId
        );
        if (index !== -1) {
          this.expenses[index] =
            this.tableConfigStore.enrichRecordsWithCustomFields(
              [updatedExpense],
              this.tableColumns
            )[0];
          this.expenses = [...this.expenses];
        }

        this.notificationStore.showSuccess("Расход подтвержден");
      } catch (error) {
        console.error("Ошибка при подтверждении расхода:", error);
      }
    },

    onSelectionChange(selected) {
      this.selectedExpenses = selected;
    },

    async duplicateExpenses(expenses) {
      if (expenses.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы одну трату"
        );
        return;
      }

      try {
        this.dataIsLoading = true;

        await Promise.all(
          expenses.map((expense) =>
            expensesApi.duplicate(this.companyId, expense.id)
          )
        );

        this.notificationStore.showSuccess(
          `Успешно продублировано ${expenses.length} расходов`
        );

        await this.getExpensesRequest();
      } catch (error) {
        console.error("Ошибка при дублировании расходов:", error);
      } finally {
        this.dataIsLoading = false;
      }
    },

    // Метод для дропдауна — проверяет selectedExpenses и делегирует
    async duplicateSelected() {
      if (this.selectedExpenses.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы одну трату"
        );
        return;
      }
      await this.duplicateExpenses(this.selectedExpenses);
    },

    async exportToExcel() {
      try {
        const search_params = filtersToApiParams(
          this.appliedFilters,
          this.searchFields
        );

        const response = await expensesApi.export(
          this.companyId,
          search_params
        );

        exportToExcel(response, "expenses.xlsx");
      } catch (error) {
        console.error("Ошибка при экспорте в Excel:", error);
      }
    },

    handleExpenseCreated() {
      this.getExpensesRequest();
      this.showAddExpensesModal = false;
    },

    handleExpenseUpdated(updatedExpense) {
      const index = this.expenses.findIndex(
        (e) => e.id === updatedExpense.id
      );

      if (index !== -1) {
        this.expenses[index] =
          this.tableConfigStore.enrichRecordsWithCustomFields(
            [updatedExpense],
            this.tableColumns
          )[0];
        this.expenses = [...this.expenses];
      }

      this.closeEditModal();
    },

    handleExpenseDeleted() {
      this.getExpensesRequest();
      this.closeEditModal();
    },

    async handleImportSuccess() {
      await this.getExpensesRequest();

      this.showImportModal = false;

      this.notificationStore.showWarning(
        "Файл поставлен в очередь на обработку. Обновите страницу позже"
      );
    },

    async handleExpenseAccepted(expenseId) {
      try {
        const response = await expensesApi.show(
          this.companyId,
          expenseId
        );
        const updatedExpense = response.data.result;

        const index = this.expenses.findIndex(
          (e) => e.id === expenseId
        );
        if (index !== -1) {
          this.expenses[index] =
            this.tableConfigStore.enrichRecordsWithCustomFields(
              [updatedExpense],
              this.tableColumns
            )[0];
          this.expenses = [...this.expenses];
        }
      } catch (error) {
        console.error("Ошибка при обновлении записи расхода:", error);
      }
    },

    goToVehicleDetails(vehicleId) {
      this.$router.push({
        name: "VehicleDetails",
        params: { id: vehicleId },
      });
    },

    goToCounterpartyDetails(counterpartyId) {
      this.$router.push({
        name: "CounterpartyDetails",
        params: { id: counterpartyId },
      });
    },

    openBulkChangeCategoryModal() {
      if (this.selectedExpenses.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы одну трату"
        );
        return;
      }
      this.bulkCategoryTarget = this.selectedExpenses;
      this.showBulkChangeCategoryModal = true;
    },

    openBulkChangeVehicleModal() {
      if (this.selectedExpenses.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы одну трату"
        );
        return;
      }
      this.bulkVehicleTarget = this.selectedExpenses;
      this.showBulkChangeVehicleModal = true;
    },

    openExpenseInNewTab(expense) {
      const expenseId = expense.id || expense;
      openRouteInNewTab({
        path: this.$route.path,
        query: { ...this.$route.query, expense_id: expenseId },
      });
    },

    handleTableContextMenuAction({ key, item }) {
      const target =
        this.selectedExpenses.length > 0
          ? this.selectedExpenses
          : [item];

      if (key === "change-category") {
        this.bulkCategoryTarget = target;
        this.showBulkChangeCategoryModal = true;
      }

      if (key === "change-vehicle") {
        this.bulkVehicleTarget = target;
        this.showBulkChangeVehicleModal = true;
      }

      if (key === "duplicate") {
        this.duplicateExpenses(target);
      }
    },

    // Меняет категорию одного расхода прямо из строки таблицы
    async handleCategoryChange(expense, categoryId) {
      try {
        await expensesApi.update(this.companyId, expense.id, {
          expense_category_id: categoryId,
        });
        const category =
          this.expenseCategoryStore.expenseCategories.find(
            (c) => String(c.id) === categoryId
          );
        expense.expense_category = category || null;
        this.notificationStore.showSuccess(
          "Категория расхода обновлена"
        );
      } catch {
        // ошибка перехватывается глобально в apiService
      }
    },

    handleBulkCategoryChanged(updatedExpenses) {
      updatedExpenses.forEach((updatedExpense) => {
        const index = this.expenses.findIndex(
          (e) => e.id === updatedExpense.id
        );
        if (index !== -1) {
          this.expenses[index] =
            this.tableConfigStore.enrichRecordsWithCustomFields(
              [updatedExpense],
              this.tableColumns
            )[0];
        }
      });

      this.expenses = [...this.expenses];

      this.showBulkChangeCategoryModal = false;
      this.bulkCategoryTarget = [];
      this.selectedExpenses = [];
      this.tableResetSelection++;
    },

    handleBulkVehicleChanged(updatedExpenses) {
      updatedExpenses.forEach((updatedExpense) => {
        const index = this.expenses.findIndex(
          (e) => e.id === updatedExpense.id
        );
        if (index !== -1) {
          this.expenses[index] =
            this.tableConfigStore.enrichRecordsWithCustomFields(
              [updatedExpense],
              this.tableColumns
            )[0];
        }
      });

      this.expenses = [...this.expenses];

      this.showBulkChangeVehicleModal = false;
      this.bulkVehicleTarget = [];
      this.selectedExpenses = [];
      this.tableResetSelection++;
    },
  },

  async created() {
    this.updateFiltersFromUrl();
    await Promise.all([
      this.loadTableConfig(),
      this.expenseCategoryStore.fetchExpenseCategories(),
    ]);
  },

  async mounted() {
    const storedFilters =
      await this.$refs.filterBlock?.loadStoredFilter();

    if (storedFilters) {
      this.appliedFilters = storedFilters;
      updateUrlWithFilters(this.$router, storedFilters, 1);
    }

    await this.getExpensesRequest();

    // Открываем модалку если в URL есть expense_id
    if (hasQueryParam("expense_id", this.$route)) {
      this.selectedExpenseId = Number(
        getQueryParam("expense_id", this.$route)
      );
      this.showEditExpensesModal = true;
    }
  },
};
</script>

<style scoped>
.no-data {
  margin-top: var(--base-in-indent);
}
</style>
